import { useCallback, useEffect, useId, useLayoutEffect, useRef } from 'react';
import classNames from 'classnames';

import ErrorText from '../ErrorText';

import styles from './_index.module.scss';

export interface TextAreaProps extends Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'onChange'
> {
  autoGrow?: boolean;
  error?: string;
  isDisabled?: boolean;
  label?: string;
  onChange: (value: string) => void;
}

const TextArea = ({
  'aria-label': ariaLabel = 'textarea',
  autoGrow = false,
  className,
  error,
  id,
  isDisabled,
  label,
  onChange,
  value,
  ...props
}: TextAreaProps) => {
  const generatedId = useId();
  const textAreaId = id ?? generatedId;

  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const initialHeightRef = useRef<null | number>(null);
  const observedWidthRef = useRef<null | number>(null);
  const resizeFrameRef = useRef<null | number>(null);

  const resizeTextArea = useCallback(() => {
    if (!autoGrow) return;
    const element = textAreaRef.current;
    if (!element) return;
    if (element.offsetWidth === 0) return;

    element.style.overflowY = 'hidden';
    element.style.height = 'auto';

    const baseHeight = initialHeightRef.current ?? element.offsetHeight;

    if (initialHeightRef.current === null) {
      initialHeightRef.current = baseHeight;
    }

    const nextHeight = Math.max(element.scrollHeight, baseHeight);
    element.style.height = `${nextHeight}px`;
  }, [autoGrow]);

  useLayoutEffect(() => {
    if (!autoGrow) return;
    resizeTextArea();
  }, [autoGrow, resizeTextArea, value]);

  useEffect(() => {
    if (!autoGrow) return;
    const element = textAreaRef.current;
    if (!element) return undefined;

    let resizeObserver: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(([entry]) => {
        const nextWidth = Math.round(entry.contentRect.width);
        if (observedWidthRef.current === nextWidth) return;
        observedWidthRef.current = nextWidth;
        if (resizeFrameRef.current) {
          window.cancelAnimationFrame(resizeFrameRef.current);
        }
        resizeFrameRef.current = window.requestAnimationFrame(() => {
          resizeFrameRef.current = null;
          resizeTextArea();
        });
      });
      resizeObserver.observe(element);
      return () => {
        if (resizeFrameRef.current) {
          window.cancelAnimationFrame(resizeFrameRef.current);
          resizeFrameRef.current = null;
        }
        resizeObserver?.disconnect();
      };
    }

    const onResize = () => resizeTextArea();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [autoGrow, resizeTextArea]);

  useEffect(() => {
    if (autoGrow) return;
    const element = textAreaRef.current;
    if (!element) return;

    element.style.height = '';
    element.style.overflowY = '';
    initialHeightRef.current = null;
    observedWidthRef.current = null;
  }, [autoGrow]);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(event.target.value);
  };

  return (
    <div>
      {label && (
        <label className={styles['textarea-label']} htmlFor={textAreaId}>
          {label}
        </label>
      )}

      <textarea
        aria-label={label ? undefined : ariaLabel}
        className={classNames(styles['textarea'], className, { [styles['is-error']]: error })}
        disabled={isDisabled}
        id={textAreaId}
        onChange={handleChange}
        ref={textAreaRef}
        value={value}
        {...props}
      />
      {error && <ErrorText className={styles['textarea-error']}>{error}</ErrorText>}
    </div>
  );
};

export default TextArea;
